import axios from 'axios';
import { toast } from 'react-toastify';

// Get baseURL from environment variable, with fallback
const getBaseURL = () => {
  const envURL = process.env.REACT_APP_API_BASE_URL;
  console.log('REACT_APP_API_BASE_URL from env:', envURL);
  if (envURL && envURL.trim() && envURL.startsWith('http')) {
    const url = envURL.trim();
    console.log('Using API Base URL:', url);
    return url;
  }
  // Fallback to production URL if env var is not set
  console.log('Using fallback API Base URL:', 'https://digitalapi.palsonsderma.com');
  return 'https://digitalapi.palsonsderma.com';
};

const baseURL = getBaseURL();

// Export baseURL so other files can use it
export { getBaseURL };

// Create a variable to store the logout function
let logoutFunction = null;

// Function to set the logout function
export const setLogoutFunction = (logout) => {
  logoutFunction = logout;
};

const api = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable sending cookies with requests
});

// Add request interceptor
api.interceptors.request.use(
  (config) => {
    // No need to manually add token as it will be sent via HTTP-only cookie
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Log the complete error response for debugging
    console.log('API Error Response:', {
      status: error.response?.status,
      data: error.response?.data,
      message: error.response?.data?.message,
      error: error.response?.data?.error
    });

    // Extract error message from various possible locations in the response
    let errorMessage = 'An error occurred';
    
    if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response?.data?.error) {
      errorMessage = error.response.data.error;
    } else if (error.message) {
      errorMessage = error.message;
    }

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401) {
      if (logoutFunction) {
        logoutFunction();
      } else {
        // Fallback if logout function is not set
        window.location.href = '/login';
      }
      toast.error('Session expired. Please login again.');
      return Promise.reject(new Error('Session expired'));
    }

    // Create a new error with the extracted message
    const customError = new Error(errorMessage);
    customError.status = error.response?.status;
    customError.data = error.response?.data;
    
    return Promise.reject(customError);
  }
);

export const getData = async (endpoint, config = {}) => {
  try {
    const response = await api.get(endpoint, {
      ...config,
      params: config.params || {}
    });
    return response.data;
  } catch (error) {
    // Log the error for debugging
    console.log('GetData Error:', {
      endpoint,
      error: error.message,
      status: error.status,
      data: error.data
    });
    throw error;
  }
};

export const postData = async (endpoint, data, config = {}) => {
  try {
    const response = await api.post(endpoint, data, config);
    return response.data;
  } catch (error) {
    console.log('PostData Error:', {
      endpoint,
      error: error.message,
      status: error.status,
      data: error.data
    });
    throw error;
  }
};

export const putData = async (endpoint, data, config = {}) => {
  try {
    const response = await api.put(endpoint, data, config);
    return response.data;
  } catch (error) {
    console.log('PutData Error:', {
      endpoint,
      error: error.message,
      status: error.status,
      data: error.data
    });
    throw error;
  }
};

export const patchData = async (endpoint, data, config = {}) => {
  try {
    const response = await api.patch(endpoint, data, config);
    return response.data;
  } catch (error) {
    console.log('PatchData Error:', {
      endpoint,
      error: error.message,
      status: error.status,
      data: error.data
    });
    throw error;
  }
};

export const deleteData = async (endpoint, config = {}) => {
  try {
    const response = await api.delete(endpoint, config);
    return response.data;
  } catch (error) {
    console.log('DeleteData Error:', {
      endpoint,
      error: error.message,
      status: error.status,
      data: error.data
    });
    throw error;
  }
};

// Upload file data (for FormData)
export const uploadData = async (endpoint, formData, config = {}) => {
  try {
    const response = await api.post(endpoint, formData, {
      ...config,
      headers: {
        'Content-Type': 'multipart/form-data',
        ...config.headers,
      },
    });
    return response.data;
  } catch (error) {
    console.log('UploadData Error:', {
      endpoint,
      error: error.message,
      status: error.status,
      data: error.data
    });
    throw error;
  }
};

// Specific function for uploading orders from Excel
export const uploadOrdersFromExcel = async (formData) => {
  return uploadData('/data/order/insert-orders-from-excel', formData);
};

// You can add more methods (putData, deleteData, etc.) as needed

export default api; 